import React, { useState, useEffect, useRef } from 'react';
import { Bell } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { getAccessToken } from '../lib/api';

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  reference_type?: string;
  reference_id?: string;
  metadata?: {
    fileRequestId?: string;
    launchRequestId?: string;
    mentionedBy?: string;
    mentionedByName?: string;
  };
  is_read: boolean;
  created_at: string;
}

export const NotificationBell: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const previousUnreadCountRef = useRef(0);
  const navigate = useNavigate();

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      const token = getAccessToken();
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 20 }
      });

      const newNotifications = response.data.notifications || [];
      const newUnreadCount = response.data.unreadCount || 0;
      const previousUnreadCount = previousUnreadCountRef.current;

      setNotifications(newNotifications);
      setUnreadCount(newUnreadCount);

      // REMOVED: Notification sounds and browser popups disabled per user request
      // Users can still see notifications in the notification bell/panel
      // Sounds and popups were interrupting workflow and not desired
      // if (newUnreadCount > previousUnreadCount && previousUnreadCount >= 0) {
      //   const latestNotification = newNotifications.find((n: Notification) => !n.is_read);
      //   if (latestNotification) {
      //     notificationSound.playNotificationSound(soundType);
      //     new Notification(latestNotification.title, { ... });
      //   }
      // }

      previousUnreadCountRef.current = newUnreadCount;
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  // Poll for new notifications every 60 seconds, pause when tab is hidden
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(() => {
      if (!document.hidden) fetchNotifications();
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Mark notification as read
  const markAsRead = async (notificationId: string) => {
    try {
      const token = getAccessToken();
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/notifications/${notificationId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setNotifications(prev =>
        prev.map(n => (n.id === notificationId ? { ...n, is_read: true } : n))
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      setLoading(true);
      const token = getAccessToken();
      await axios.post(
        `${process.env.REACT_APP_API_URL}/notifications/mark-all-read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle notification click
  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }

    // Navigate based on notification type
    if (notification.type === 'access_request') {
      navigate('/access-requests?tab=to-review');
      setShowDropdown(false);
      return;
    }

    if (notification.type === 'access_request_approved' || notification.type === 'access_request_denied') {
      navigate('/access-requests?tab=my-requests');
      setShowDropdown(false);
      return;
    }

    // File request notifications
    if (notification.reference_type === 'file_request' && notification.reference_id) {
      navigate(`/file-requests?openRequestId=${notification.reference_id}`);
      setShowDropdown(false);
      return;
    }

    // Launch request notifications
    if (notification.reference_type === 'launch_request' && notification.reference_id) {
      navigate(`/launch-requests?openRequestId=${notification.reference_id}`);
      setShowDropdown(false);
      return;
    }

    // Launch request type-based routing
    if (notification.type === 'launch_request_submitted' ||
        notification.type === 'launch_request_accepted' ||
        notification.type === 'launch_request_ready' ||
        notification.type === 'launch_request_assigned' ||
        notification.type === 'launch_request_launched' ||
        notification.type === 'launch_request_closed' ||
        notification.type === 'launch_request_updated') {
      const id = notification.reference_id || notification.metadata?.launchRequestId;
      if (id) {
        navigate(`/launch-requests?openRequestId=${id}`);
      } else {
        navigate('/launch-requests');
      }
      setShowDropdown(false);
      return;
    }

    // Media file notifications
    if (notification.reference_type === 'file' && notification.reference_id) {
      navigate(`/media/${notification.reference_id}`);
      setShowDropdown(false);
      return;
    }

    if (notification.reference_type === 'canvas' && notification.metadata?.fileRequestId) {
      navigate(`/dashboard?requestId=${notification.metadata.fileRequestId}&openCanvas=true`);
      setShowDropdown(false);
      return;
    }

    // Fallback
    setShowDropdown(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Format time ago
  const formatTimeAgo = (timestamp: string) => {
    const now = new Date();
    const then = new Date(timestamp);
    const seconds = Math.floor((now.getTime() - then.getTime()) / 1000);

    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return then.toLocaleDateString();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-red-600 rounded-full">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {showDropdown && (
        <div className="absolute right-0 mt-2 w-96 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-[32rem] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between bg-gray-50">
            <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                disabled={loading}
                className="text-xs text-blue-600 hover:text-blue-700 font-medium disabled:opacity-50"
              >
                {loading ? 'Marking...' : 'Mark all as read'}
              </button>
            )}
          </div>

          {/* Notifications list */}
          <div className="overflow-y-auto flex-1">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-500 text-sm">
                <Bell className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>No notifications yet</p>
              </div>
            ) : (
              notifications.map(notification => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`px-4 py-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${
                    !notification.is_read ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-sm font-medium text-gray-900 truncate">
                          {notification.title}
                        </h4>
                        {!notification.is_read && (
                          <span className="flex-shrink-0 w-2 h-2 bg-blue-600 rounded-full" />
                        )}
                      </div>
                      {notification.message && (
                        <p className="text-sm text-gray-600 line-clamp-2 mb-1">
                          {notification.message}
                        </p>
                      )}
                      <p className="text-xs text-gray-400">
                        {formatTimeAgo(notification.created_at)}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-2 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => {
                  navigate('/notifications');
                  setShowDropdown(false);
                }}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium w-full text-center"
              >
                View all notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
